/* eslint-disable react/jsx-filename-extension */
/* eslint-disable react/prop-types */
import { assertIsDefined } from '@bingads-webui-campaign-react/debug-assert';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { DefaultButton, Dropdown, IColumn, IDropdownOption, Label, Panel, PanelType, PrimaryButton, ShimmeredDetailsList, Stack, Selection, IGroup, IDetailsListCheckboxProps, IRenderFunction, mergeStyles, CheckboxVisibility, CollapseAllVisibility, CommandBarButton, IIconProps, IDropdownProps, MessageBar } from '@fluentui/react';
import { useId, usePrevious } from '@fluentui/react-hooks';
import { Loading } from '@bingads-webui-campaign-react/loading';
import { ListViewSelectionZone } from '@bingads-webui-grid/well-known';
import React, { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Paginator } from './paginator';
import { BulkAddEditorProps, BulkAddEditor } from './BulkAddEditor';

const panelStyles = {
  main: {
    top: 60,
  },
  content: {
    paddingLeft: 0,
    paddingRight: 0,
    display: 'flex',
    overflow: 'hidden',
  },
  footerInner: {
    padding: 0,
    borderTop: '1px solid #EDEBE9',
  },
} as const;

const overlayProps = {
  styles: {
    root: {
      top: 60,
    },
  },
};

const panelSelectorStyleClassName = mergeStyles({
  '& .content': {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    '& .divider': {
      display: 'flex',
      marginLeft: 28,
      marginTop: 16,
      marginBottom: 16,
      '& .divider-dropdown': {
        marginLeft: 8,
        width: 220,
      },
    },
    '& .bulk-value-editor-toggler': {
      height: 32,
      marginLeft: 28,
      marginTop: 16,
      marginBottom: 6,
    },
    '& .grid': {
      overflow: 'hidden',
      display: 'flex',
      flex: 1,
      flexDirection: 'column',
      '& .list-view-selection-zone': {
        display: 'flex',
        flex: 1,
        overflow: 'hidden',
      },
      '& .ms-Viewport': {
        display: 'flex',
        flex: 1,
        '& .ms-DetailsList': {
          marginBottom: 0,
          '& div[role=grid]': {
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            '& .ms-DetailsList-contentWrapper': {
              overflowY: 'overlay',
              overflowX: 'hidden',
              '.ms-List-cell': {
                minHeight: 0, // empty group should not ocupy any space
              },
            },
          },
        },
      },
      '& .paginator': {
        marginLeft: 16,
        marginTop: 12,
      },
    },
    '& .error-region': {
      color: '#A80000',
    },
  },
});

const onRenderSquareCheckbox: IRenderFunction<IDetailsListCheckboxProps> = (props) => {
  assertIsDefined(props, 'props of checkbox render is undefined');
  const { checked } = props;

  // Do not use Chekbox of FluentUI. It is hard to manage events when working with `SelectionZone`(used by DetailsList)
  return (<input type="checkbox" className={`ms-Checkbox ${checked ? 'is-checked' : ''}`} checked={checked} />);
};

const addIcon: IIconProps = { iconName: 'Add' };

export type WithBulk<T = any> =
  { __bulk?: boolean; } & T;

export interface PanelSelectorProps {
  enableShimmer?: boolean;
  isOpen: boolean;
  /**
   *
   */
  onSave: (selectedItems: WithBulk[], selectedDimension: IDropdownOption<any>) => void;
  onCancel: () => void;
  /**
   * Dimension Dropdown
   *
   * Control this variable to show default selected dimension
   */
  dimensions: IDropdownOption<any>[];
  onSelectedDimensionChange: NonNullable<IDropdownProps['onChange']>;
  columns: IColumn[];
  /**
   * If the key of item is a negative number, it is added by `bulk add values`
   */
  items: any[];
  initialSelectedItems: any[];
  headerText: string;
  keyOfItem: (item: any) => string;
  /**
   * Some dimensions are avaliable to give arbitry values to divide them.
   *
   * If so, enable this flag so user could input arbitry values.
   */
  isBulkAddEnabledForDimension: (diemnsion: IDropdownOption<any>) => boolean;

  // Error handing
  getErrors?: (selectedItems: any[], selectedDimension: IDropdownOption<any>) => string[];
}

function copyAndSort<T>(items: T[], columnKey: string, isSortedDescending?: boolean): T[] {
  const key = columnKey as keyof T;
  return items.slice(0).sort((a, b) => {
    const aValue = a[key];
    const bValue = b[key];
    let res = 0;
    if (aValue !== undefined && bValue !== undefined) {
      res = aValue > bValue ? 1 : -1;
    } else if (aValue === undefined && bValue === undefined) {
      res = 0;
    } else {
      // assume undefined as -Infinate
      res = aValue !== undefined ? 1 : -1;
    }
    return isSortedDescending ? -res : res;
  });
}

export const PanelSelector: FC<PanelSelectorProps> = (props) => {
  const {
    enableShimmer, isOpen, headerText,
    columns, items, dimensions,
    onCancel, onSave, onSelectedDimensionChange,
    initialSelectedItems, keyOfItem, isBulkAddEnabledForDimension,
    getErrors,
  } = props;
  const i18n: any = useContext(I18nContext);

  const preItems = usePrevious(items);
  const preColumns = usePrevious(columns);
  const preDimensions = usePrevious(dimensions);
  const preInitialSelectedItems = usePrevious(initialSelectedItems);

  const [finalItems, setFinalItemsImpl] = useState(items);
  const setFinalItems: typeof setFinalItemsImpl = (param) => {
    let finalValue;
    if (typeof param === 'function') {
      setFinalItemsImpl((pre) => {
        finalValue = param(pre);
        return finalValue;
      });
    } else {
      finalValue = param;
      setFinalItemsImpl(finalValue);
    }

    // / sync items and selected to selection
    // when dimension is still same, if all items are all selected in pre state, keep all selected
    // when dimension is changed, only when next dimension is default dimension and `initialSelectedItems` is `finalItems`, it is all selected

    const defaultSelectedDimension = getDefaultSelectedDimension();
    assertIsDefined(defaultSelectedDimension);
    assertIsDefined(selectedDimension);
    const isSelectedDimensionChanged = previousSelectedDimension?.key !== selectedDimension.key;

    const isCurrentAllSelected = selection.isAllSelected();
    let isNextAllSelect = false;
    const isDefaultDimensionSelected = selectedDimension.key === defaultSelectedDimension.key;
    if (isDefaultDimensionSelected && initialSelectedItems.length !== 0) {
      isNextAllSelect = initialSelectedItems.length === finalValue.length; // a quick and dirty check
    }

    const isEventSupressed = isSelectedDimensionChanged ? isCurrentAllSelected === isNextAllSelect : true;

    selection.setChangeEvents(false);
    if (finalValue) {
      selection.setItems(finalValue, true);
    }
    selection.setChangeEvents(true, isEventSupressed);
    if (isNextAllSelect) {
      setSelectedItemKeys(initialSelectedItems.map(keyOfItem));
    }
    setSelectedItemKeys((selectedItemKeys) => {
      selection.setChangeEvents(false);
      selectedItemKeys.forEach((key) => {
        selection.setKeySelected(key, true, false);
      });
      selection.setChangeEvents(true, isEventSupressed);
      return selectedItemKeys;
    });
  };

  const [finalColumns, setFinalColumns] = useState(columns);

  const [selectedItemKeys, setSelectedItemKeys] = useState<string[]>([]);

  const selection = useMemo(() => new Selection({
    onSelectionChanged: () => {
      // The main purpose is to sync user operation to `selectedItemKeys`
      setSelectedItemKeys((pre) => {
        const newSelectedItemKeysCandidate = selection.getSelection().map(keyOfItem);
        if (pre.join() !== newSelectedItemKeysCandidate.join()) { return newSelectedItemKeysCandidate; }
        return pre;
      });
    },
    getKey: keyOfItem,
    items: finalItems,
  }), []);

  useEffect(() => {
    // if dimensions changes, update selectedDimension
    // if defualt selected dimension changes, update selectedDimension
    if (
      dimensions.map(dimension => dimension.key).join() !== (preDimensions?.map(dimension => dimension.key) ?? []).join()
      || dimensions.map(dimension => dimension.selected).join() !== (preDimensions?.map(dimension => dimension.selected) ?? []).join()
    ) {
      setSelectedDimension(getDefaultSelectedDimension());
    }

    // if items chagnes, update finalItems
    if (items.map(keyOfItem).join() !== (preItems?.map(keyOfItem) ?? []).join()) {
      setFinalItems(items);
    }

    // if initialSelectedItems changes, update selectedItemKeys
    if (initialSelectedItems.map(keyOfItem).join() !== preInitialSelectedItems?.map(keyOfItem).join()) {
      setSelectedItemKeys(initialSelectedItems.map(keyOfItem));
    }

    // if selection does not match selectedItemKeys, sync to selectedItemKeys
    if (selectedItemKeys.join() !== selection.getSelection().map(keyOfItem).join()) {
      selection.setChangeEvents(false);
      selectedItemKeys.forEach((key) => {
        selection.setKeySelected(key, true, false);
      });
      selection.setChangeEvents(true, true);
    }

    // if columns changes, update finalColumns
    if (columns.map(c => c.key).join() !== (preColumns?.map(c => c.key) ?? []).join()) {
      setFinalColumns(columns);
    // eslint-disable-next-line brace-style
    }
    // if column changes, we do not need to update binding of finalColumns
    else {
      // bind column click
      finalColumns.forEach((finalColumn) => {
        // eslint-disable-next-line no-param-reassign
        finalColumn.onColumnClick = (ev, column) => {
          // bind sort
          // eslint-disable-next-line @typescript-eslint/no-shadow
          setFinalColumns((preColumns) => {
            let { isSortedDescending } = column;
            if (column.isSorted) {
              isSortedDescending = !isSortedDescending;
            }
            setFinalItems(pre =>
              copyAndSort(pre, column.fieldName!, isSortedDescending));
            return preColumns.map(preColumn => ({
              ...preColumn,
              isSorted: preColumn.key === column.key,
              isSortedDescending: preColumn.key === column.key ? isSortedDescending : undefined,
            }));
          });
        };
      });
    }
  });

  const dimensionSelectorId = useId('divide-by');

  const [currentPageSize, setCurrentPageSize] = useState(20);
  const [currentPageIndex, setCurrentPageIndex] = useState(1);

  // use groups as false pages
  const pageCount = Math.ceil(finalItems.length / currentPageSize);

  const groups: IGroup[] = Array(pageCount).fill(null).map((_, i) => ({
    key: i.toString(), // maybe we should add currentPageSize and currentPageIndex as part of key?
    name: '', // name is not important, the group header should never be rendered.
    startIndex: i * currentPageSize,
    count: currentPageSize,
    isCollapsed: i + 1 !== currentPageIndex, // page is base-1, array index is base 0
  }));

  function onSaveClick() {
    const selectedItems = finalItems.filter(item => selectedItemKeys.includes(keyOfItem(item)));
    assertIsDefined(selectedDimension);
    onSave(selectedItems, selectedDimension);
  }

  const onRenderFooterContent = () => (
    <div className="footer">
      <Stack horizontal tokens={{ childrenGap: 12, padding: 16 }}>
        <PrimaryButton data-test-id="save-button" disabled={!!errors?.length} onClick={onSaveClick}>{i18n.getString(_TL_('Save'))}</PrimaryButton>
        <DefaultButton data-test-id="cancel-button" onClick={onCancel}>{i18n.getString(_TL_('Cancel'))}</DefaultButton>
      </Stack>
    </div>
  );

  const isLoading = enableShimmer;

  const [isBulkAddEditorOpened, setIsBulkAddEditorOpened] = useState(false);

  const [selectedDimension, setSelectedDimension] = useState(getDefaultSelectedDimension);
  const previousSelectedDimension = usePrevious(selectedDimension);

  const errors = isLoading ? [] : getErrors?.(
    finalItems.filter(item => selectedItemKeys.includes(keyOfItem(item))),
    selectedDimension!
  );

  /** Always negative and not repeat, chooser a big enough number to avoid conflict with ID from API  */
  const customCounter = useRef(-100000);

  function onClickBulkAddValues() {
    setIsBulkAddEditorOpened(true);
  }

  const onBulkAddEditorCancel: BulkAddEditorProps['onCancel'] = () => {
    setIsBulkAddEditorOpened(false);
  };

  function buildNewItemsByBulkAdd(strings: string[]) {
    return strings.map(s => ({
      __bulk: true,
      key: customCounter.current--,
      [firstColumnName]: s,
    }));
  }

  const onBulkAddEditorCommit: BulkAddEditorProps['onSubmit'] = (newValues) => {
    // Note: for current, `PanelSelector` do not do anything like deduplicate, so user might have multi same values
    // The callback should make sure the situation is handled correctly
    const newItems = buildNewItemsByBulkAdd(newValues);
    setFinalItems(pre => [...newItems, ...pre]);
    setSelectedItemKeys((pre) => {
      const newKeys = newItems.map(item => keyOfItem(item));
      const newSelectedItemKeys = [...pre, ...newKeys];

      selection.setChangeEvents(false);
      newSelectedItemKeys.forEach((key) => {
        selection.setKeySelected(key, true, false);
      });
      selection.setChangeEvents(true);

      return newSelectedItemKeys;
    });
    setIsBulkAddEditorOpened(false);
  };

  function renderBulkValueEditor() {
    return (
      <div className="bulk-value-editor-wrapper">
        {selectedDimension && isBulkAddEnabledForDimension(selectedDimension) &&
          (!isBulkAddEditorOpened ?
            <CommandBarButton className="bulk-value-editor-toggler" iconProps={addIcon} onClick={onClickBulkAddValues} text={i18n.getString(_TL_('Bulk add values'))} />
            : <BulkAddEditor onCancel={onBulkAddEditorCancel} onSubmit={onBulkAddEditorCommit} />)
        }
      </div>
    );
  }

  function getDefaultSelectedDimension() {
    return dimensions.find(dimension => dimension.selected === true);
  }

  const onSelectedDimensionChangeInner: IDropdownProps['onChange'] = (event, option, index) => {
    setSelectedDimension(option!);
    onSelectedDimensionChange(event, option, index);
    setCurrentPageIndex(1);
  };


  function renderErrorMessages() {
    return (
      <div className="error-region">
        {errors?.map(error => (<div>{error}</div>))}
      </div>
    );
  }

  return (
    <Panel
      className={`panel-selector ${panelSelectorStyleClassName}`}
      type={PanelType.medium}
      isOpen={isOpen}
      styles={panelStyles}
      overlayProps={overlayProps}
      // header
      closeButtonAriaLabel={i18n.getString(_TL_('Close'))}
      headerText={headerText}
      onDismiss={onCancel}
      // footer
      isFooterAtBottom
      onRenderFooterContent={onRenderFooterContent}
    >
      <div className="content" >
        {isLoading ?
          <Loading text={i18n.getString(_TL_('Loading...'))} isVisible />
          :
          <React.Fragment>
            <div className="divider">
              <Label htmlFor={dimensionSelectorId}>{i18n.getString(_TL_('Divide by:'))}</Label>
              <Dropdown
                className="divider-dropdown"
                selectedKey={selectedDimension?.key}
                options={dimensions}
                id={dimensionSelectorId}
                onChange={onSelectedDimensionChangeInner}
              />
            </div>
            {renderBulkValueEditor()}
            {finalItems.length === 0 &&
            <MessageBar
              dismissButtonAriaLabel="Close"
            >
              {i18n.getString(_TL_('No suggestions available. Please add values manually.'))}
            </MessageBar>
            }
            <div className="grid">
              {/* After Fluent UI is updated to a version that have `selectionClearedOnSurfaceClick` in `SelectionZone` */}
              {/* We should deprecated `ListViewSelectionZone` immediately. */}
              <ListViewSelectionZone
                className="list-view-selection-zone"
                selection={selection}
                selectionClearedOnSurfaceClick={false}
              >
                <ShimmeredDetailsList
                  selection={selection}
                  setKey="set"
                  className="table"
                  columns={finalColumns}
                  items={finalItems}
                  enableShimmer={enableShimmer}
                  onRenderCheckbox={onRenderSquareCheckbox}
                  checkboxVisibility={CheckboxVisibility.always}
                  groups={groups}
                  groupProps={{
                    onRenderHeader: () => null, // only show items in current page
                    collapseAllVisibility: CollapseAllVisibility.hidden,
                  }}
                  disableSelectionZone
                />
              </ListViewSelectionZone>
              {finalItems.length > 0 && <Paginator
                className="paginator"
                currentPage={currentPageIndex}
                onPageIndexChange={setCurrentPageIndex}
                onPageSizeChange={setCurrentPageSize}
                pageSize={currentPageSize}
                totalCount={finalItems.length}
              />}
            </div>
            {renderErrorMessages()}
          </React.Fragment>
        }
      </div>
    </Panel>
  );
};

/**
 * This is exported for `bulk add`
 *
 * Or we could export another callback for `bulk add value`, then let users to change items by themself, but it is kind of heavy.
 */
export const firstColumnName = 'primary';
